import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { rowDescriptor } from '../../lib/kindMap';
import { describeGroup, type ActivityGroup } from '../../lib/grouping';
import { ActivityEventRow } from './ActivityEventRow';

interface ActivityGroupRowProps {
  group: ActivityGroup;
  /** False on the very last row rendered, so the timeline connector does not dangle into the footer. */
  showConnector: boolean;
}

/**
 * A collapsed run of related rows, with the individual rows one click away.
 *
 * ⚠️ **Nothing is merged, summed or synthesised.** The owner's rule is that one submission is one row; this
 * is a container around rows that all still exist, each keeping its own actor, time and evidence. The header
 * counts what is inside it and nothing else — no server total, no arithmetic over quantities.
 *
 * Expansion is local state, deliberately. A parent-held expansion map would re-render every row in the feed
 * on every expand, and the cost grows the longer the reader has been paging.
 */
export function ActivityGroupRow({ group, showConnector }: ActivityGroupRowProps) {
  const [expanded, setExpanded] = useState(false);

  // The group's tint comes from its FIRST row. A mixed-kind group (E-1 collapses a pick and a fulfilment
  // together) has no single honest answer, and the first row is the one the reader would have seen anyway.
  const { icon: Icon, tile } = rowDescriptor(group.rows[0]);
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const summary = describeGroup(group);

  if (expanded) {
    return (
      <>
        <li className="px-3 pt-2">
          <button
            type="button"
            aria-expanded
            onClick={() => setExpanded(false)}
            className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ring-offset-background"
          >
            <ChevronDown className="h-3 w-3" aria-hidden />
            Collapse {group.rows.length} events
          </button>
        </li>

        {group.rows.map((entry, index) => (
          <ActivityEventRow
            key={entry.id}
            entry={entry}
            showConnector={showConnector || index < group.rows.length - 1}
          />
        ))}
      </>
    );
  }

  return (
    <li className="flex gap-2.5 px-3 py-2 transition-colors hover:bg-muted/45 motion-reduce:transition-none">
      <div className="flex shrink-0 flex-col items-center">
        {/* Prototype-exact tile chrome (fidelity spec 86d43a6v3 PART 3a) — identical to ActivityEventRow's,
            because the prototype's two row kinds share the tile column byte-for-byte. */}
        <span aria-hidden className={cn('flex h-6 w-6 items-center justify-center rounded-[7px]', tile)}>
          <Icon className="h-[13px] w-[13px]" />
        </span>
        {showConnector && <span aria-hidden className="mt-1 w-px flex-1 bg-border" />}
      </div>

      {/* Prototype-exact button geometry (fidelity spec 86d43a6v3 PART 3e). ⚠️ `items-start` + the
          chevron's `mt-[3px]` + the wrapper's `pt-[3px]` are load-bearing once the text clamps to two
          lines: with `items-center` a two-line header floats the chevron BETWEEN the lines instead of
          anchoring it to the first. */}
      <div className="min-w-0 flex-1 pt-[3px]">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-expanded={false}
              onClick={() => setExpanded(true)}
              className="flex w-full items-start gap-[5px] text-left text-[12.5px] leading-[1.55] text-muted-foreground transition-colors hover:text-foreground motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ring-offset-background"
            >
              <Chevron className="mt-[3px] h-3 w-3 shrink-0" aria-hidden />
              {/* Clamped at two lines, never truncated at one: a correlation header now NAMES its changes
                  (§1), and an ellipsis after the first would hide exactly the information the sentence
                  exists to surface. The tooltip carries the whole thing for the long tail. */}
              <span className="min-w-0 flex-1 line-clamp-2">{summary}</span>
              {/* The log-count chip (§3, PART 3e). `shrink-0` is load-bearing: the chip is the THIRD flex
                  child after the chevron and the clamped text, so without it the chip is what gives way
                  when the sentence is long — exactly when the reader most wants the count. Inside the
                  button so it shares the click target and the hover colour. */}
              <span className="inline-flex h-[17px] shrink-0 items-center rounded-full bg-muted px-[7px] text-[10.5px] font-semibold tabular-nums">
                {group.rows.length} logs
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[270px] whitespace-normal">
            {summary}
          </TooltipContent>
        </Tooltip>
      </div>
    </li>
  );
}
